import { useMemo, Fragment } from 'react';
import { useQuery } from 'react-query';
import { FormControlLabel, Checkbox, Grid, MenuItem, TextField, Switch } from '@mui/material';
import { IPeopleAgent, IPeopleBuilds } from '../../../models';
import { formatInputPhoneNumber } from '../../../helpers';
import { getPeopleBuilds } from '../../../fetch';
import { getAgentsLookup } from '../../../fetch/lookups';

import { Loader, SMBSelect, StatesDropdown } from '../../../components';
import { AccordionList } from '../../../components/accordion/simple-accordion-list';

export const ClientFields = ({
  setFieldValue,
  isFormDisabled,
  values,
  handleBlur,
  touched,
  errors,
  currentExternalPersonId,
  showContactInfo = true,
  showAdditionalFields = false
}) => {
  const { isLoading: isLoadingAgents, data: agentsData } = useQuery<IPeopleAgent[], Error>(
    ['agents'],
    () => getAgentsLookup(true)
  );
  const agents = useMemo(() => agentsData ?? [], [agentsData]);

  const { isLoading: isLoadingBuilds, data: buildData } = useQuery<
    IPeopleBuilds[] | undefined,
    Error
  >(['peopleBuilds', currentExternalPersonId], () => getPeopleBuilds(currentExternalPersonId), {
    enabled: !!currentExternalPersonId
  });

  const builds = useMemo(() => buildData ?? [], [buildData]);

  return (
    <>
      <Grid item xs={12} sm={6}>
        <SMBSelect
          variant="outlined"
          required
          onChange={e => {
            setFieldValue('agentId', e.target.value);
          }}
          disabled={isLoadingAgents || isFormDisabled}
          name={`agentId`}
          id={`agentId`}
          label="Agent"
          handleClear={() => setFieldValue('agentId', '')}
          value={values.agentId}
        >
          {agents &&
            agents.map((agent: IPeopleAgent, index) => {
              return (
                <MenuItem key={`${index}`} value={agent.personId}>
                  {agent.firstName} {agent.lastName} {agent.workplace ?? ''}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="First Name"
              name="firstName"
              value={values.firstName}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              inputProps={{
                'data-lpignore': 'true'
              }}
              error={touched.firstName && errors && errors.firstName ? true : false}
              helperText={touched.firstName && errors && errors.firstName}
              onChange={e => setFieldValue('firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Last Name"
              name="lastName"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.lastName}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.lastName && errors && errors.lastName ? true : false}
              helperText={touched.lastName && errors && errors.lastName}
              onChange={e => setFieldValue('lastName', e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          required
          variant="outlined"
          autoComplete="nope"
          label="Contract Name"
          name="contractName"
          inputProps={{
            'data-lpignore': 'true'
          }}
          value={values.contractName}
          disabled={isFormDisabled}
          onBlur={handleBlur}
          size="small"
          error={touched.contractName && errors && errors.contractName ? true : false}
          helperText={touched.contractName && errors && errors.contractName}
          onChange={e => setFieldValue('contractName', e.target.value)}
        />
      </Grid>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('email', e.target.value)}
              error={touched.email && errors && errors.email ? true : false}
              helperText={touched.email && errors && errors.email}
              fullWidth={true}
              id="email"
              name="email"
              onBlur={handleBlur}
              label="Email"
              size="small"
              required={true}
              autoComplete="off"
              value={values.email}
              disabled={isFormDisabled}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Phone Number"
              name="phone"
              value={values.phoneNumber}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.phoneNumber && errors.phoneNumber)}
              helperText={touched.phoneNumber && errors.phoneNumber}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Address Line 1"
              name="address"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.address}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.address && errors && errors.address ? true : false}
              helperText={touched.address && errors && errors.address}
              onChange={e => setFieldValue('address', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Address Line 2"
              name="address2"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.address2}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.address2 && errors && errors.address2 ? true : false}
              helperText={touched.address2 && errors && errors.address2}
              onChange={e => setFieldValue('address2', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="City"
              name="city"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.city}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.city && errors && errors.city ? true : false}
              helperText={touched.city && errors && errors.city}
              onChange={e => setFieldValue('city', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatesDropdown
              value={values.state}
              handleChange={value => {
                setFieldValue('state', value as string);
              }}
              isAdminPortal={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="ZIP"
              name="zip"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.zip}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.zip && errors && errors.zip ? true : false}
              helperText={touched.zip && errors && errors.zip}
              onChange={e => setFieldValue('zip', e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <FormControlLabel
          color="primary"
          control={
            <Checkbox
              checked={values.buyerHasAgent}
              id="buyerHasAgent"
              onChange={(_, checked) => setFieldValue('buyerHasAgent', checked)}
            />
          }
          label="Buyer Has Agent"
        />
      </Grid>
      {values.buyerHasAgent && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('agentFirstName', e.target.value)}
              fullWidth={true}
              id="email"
              label="Selling Agent First Name"
              size="small"
              autoComplete="off"
              value={values.agentFirstName}
              disabled={isFormDisabled}
              name="agentFirstName"
              onBlur={handleBlur}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('agentLastName', e.target.value)}
              fullWidth={true}
              id="email"
              label="Selling Agent Last Name"
              size="small"
              autoComplete="off"
              name="agentLastName"
              onBlur={handleBlur}
              value={values.agentLastName}
              disabled={isFormDisabled}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('agentEmail', e.target.value)}
              fullWidth={true}
              id="email"
              error={touched.agentEmail && errors && errors.agentEmail ? true : false}
              helperText={touched.agentEmail && errors && errors.agentEmail}
              label="Selling Agent Email"
              size="small"
              autoComplete="off"
              name="agentEmail"
              onBlur={handleBlur}
              value={values.agentEmail}
              disabled={isFormDisabled}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              onChange={e => setFieldValue('agentPhone', formatInputPhoneNumber(e.target.value))}
              fullWidth={true}
              id="email"
              error={touched.agentPhone && errors && errors.agentPhone ? true : false}
              helperText={touched.agentPhone && errors && errors.agentPhone}
              label="Selling Agent Phone"
              size="small"
              autoComplete="off"
              name="agentPhone"
              onBlur={handleBlur}
              value={values.agentPhone}
              disabled={isFormDisabled}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
            />
          </Grid>
          {showAdditionalFields && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  onChange={e =>
                    setFieldValue('agentSellingCompany', formatInputPhoneNumber(e.target.value))
                  }
                  fullWidth={true}
                  id="email"
                  error={
                    touched.agentSellingCompany && errors && errors.agentSellingCompany
                      ? true
                      : false
                  }
                  helperText={touched.agentSellingCompany && errors && errors.agentSellingCompany}
                  label="Selling Company"
                  size="small"
                  autoComplete="off"
                  name="agentSellingCompany"
                  onBlur={handleBlur}
                  value={values.agentSellingCompany}
                  disabled={isFormDisabled}
                  inputProps={{
                    'data-lpignore': true // Last Pass disable auto-fill
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={values.hideBuilds}
                      id="hide-builds"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                        setFieldValue('hideBuilds', checked);
                      }}
                    />
                  }
                  label="Hide Builds"
                />
              </Grid>
            </>
          )}
        </>
      )}
      <Grid item xs={12}>
        {isLoadingBuilds && <Loader type={'inline'} position="centered" />}
        {!isLoadingBuilds && builds.length > 0 && (
          <AccordionList
            pluralItemName={'Builds'}
            title={`Builds (${Array.from(new Set(builds)).length})`}
            items={builds.map(build => build.neighborhoodDisplay)}
          />
        )}
      </Grid>
    </>
  );
};
